// src/features/brain/useBrainStream.ts
// Phase 6.6 Bundle 3.1 - SSE streaming hook for brain.query.
// Bundle 6.7.5 - Stream resilience rewrite.
//
// Calls ai-router with feature_key='brain.query'. Reads the SSE response
// stream, parses each `data: {...}\n\n` chunk, and surfaces:
//   - onToken(text): each text delta as it arrives
//   - onDone({ conversationId, citations }): final event after stream end
//   - onIncomplete(info): stream ended abnormally AFTER content arrived;
//     carries the accumulated partial text plus diagnostics
//   - onError(message): failure with no usable content
//
// 6.7.5 changes:
//   1. Idle watchdog: aborts if no first byte within 60s or no data for
//      30s mid-stream, instead of hanging with the composer disabled.
//   2. Terminal-state classification: completed / incomplete / failed /
//      cancelled. Partial content is never silently discarded.
//   3. Residual buffer flush: events that arrive without a trailing blank
//      line before the connection closes (including the final done event)
//      are now parsed instead of dropped. Suspected root cause of the
//      "first try fails, retry works" failure on long responses.
//   4. The hook accumulates the streamed text itself so onIncomplete can
//      hand the caller the exact partial content for persistence.
//   5. Diagnostics (token count, elapsed ms, close reason) logged via
//      console.warn on abnormal ends to confirm root cause from the field.
//
// The server-side handler (handleBrainQuery in ai-router) saves the user
// and assistant messages itself on success; this hook only consumes the
// stream. On abnormal ends the caller decides persistence (see BrainChat).
//
// Auth: real user JWT from supabase.auth.getSession(). NOT the service role.

import { useCallback, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useOrgStore } from "@/stores/orgStore";
import type { BrainCitation } from "./useBrainQueries";

// No first byte within this window -> abort, classify as timeout.
const FIRST_BYTE_TIMEOUT_MS = 60000;
// No data for this long after the first byte -> abort, classify as stalled.
const IDLE_TIMEOUT_MS = 30000;

export type BrainStreamRequest = {
  question: string;
  conversationId: string | null;
  // Phase 6.8: the user's UI locale; the server keys response language on it.
  locale?: string;
  conversationHistory: Array<{
    role: "user" | "assistant";
    content: string;
  }>;
};

export type BrainStreamIncompleteReason =
  | "stalled"
  | "stream_error"
  | "closed_without_done";

export type BrainStreamIncomplete = {
  partialText: string;
  conversationId: string | null;
  reason: BrainStreamIncompleteReason;
  diagnostics: {
    tokenCount: number;
    elapsedMs: number;
    detail: string;
  };
};

export type BrainStreamCallbacks = {
  onToken?: (text: string) => void;
  onDone?: (result: {
    conversationId: string;
    citations: BrainCitation[];
    // Phase 6.8: true when the server hit max_tokens (response cut short).
    truncated?: boolean;
  }) => void;
  onIncomplete?: (info: BrainStreamIncomplete) => void;
  onError?: (message: string) => void;
};

// SSE chunks emitted by the ai-router brain.query handler:
//   { type: "start", conversation_id }  - Phase 6.8: first event; carries the
//     conversation id so partials persist even for brand-new conversations
//   { type: "token", text: "..."  }   - incremental text delta
//   { type: "done", conversation_id, citations, truncated }
//   { type: "error", message }
type SseEvent =
  | { type: "start"; conversation_id: string }
  | { type: "token"; text: string }
  | {
      type: "done";
      conversation_id: string;
      citations: BrainCitation[];
      truncated?: boolean;
    }
  | { type: "error"; message: string };

export function useBrainStream() {
  const orgId = useOrgStore((s) => s.currentOrgId);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Distinguishes a deliberate user cancel from a watchdog abort; both
  // surface as AbortError in the catch block.
  const userCancelledRef = useRef(false);
  const watchdogFiredRef = useRef(false);

  const sendQuestion = useCallback(
    async (
      request: BrainStreamRequest,
      callbacks: BrainStreamCallbacks = {},
    ): Promise<void> => {
      if (!orgId) {
        callbacks.onError?.("No active organization");
        return;
      }

      // Capture user JWT from current session.
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) {
        callbacks.onError?.("Not signed in");
        return;
      }

      userCancelledRef.current = false;
      watchdogFiredRef.current = false;

      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);

      const startedAt = Date.now();
      let tokenCount = 0;
      let fullText = "";
      let gotDone = false;
      let gotServerError = false;
      let conversationId: string | null = request.conversationId;

      let watchdogTimer: ReturnType<typeof setTimeout> | undefined;
      const armWatchdog = (ms: number) => {
        if (watchdogTimer) clearTimeout(watchdogTimer);
        watchdogTimer = setTimeout(() => {
          watchdogFiredRef.current = true;
          controller.abort();
        }, ms);
      };
      const disarmWatchdog = () => {
        if (watchdogTimer) clearTimeout(watchdogTimer);
        watchdogTimer = undefined;
      };

      const finishIncomplete = (
        reason: BrainStreamIncompleteReason,
        detail: string,
      ) => {
        console.warn("[brain] stream incomplete", {
          reason,
          detail,
          tokenCount,
          elapsedMs: Date.now() - startedAt,
        });
        callbacks.onIncomplete?.({
          partialText: fullText,
          conversationId,
          reason,
          diagnostics: {
            tokenCount,
            elapsedMs: Date.now() - startedAt,
            detail,
          },
        });
      };

      // Parses one raw SSE event block (lines, possibly multiple data:
      // lines). Shared by the main read loop and the final buffer flush.
      const processEvent = (rawEvent: string) => {
        const lines = rawEvent.split("\n");
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (!data || data === "[DONE]") continue;

          let parsed: SseEvent;
          try {
            parsed = JSON.parse(data) as SseEvent;
          } catch {
            continue;
          }

          if (parsed.type === "start") {
            // Phase 6.8: capture the conversation id immediately so
            // onIncomplete carries it even if the stream dies before done.
            conversationId = parsed.conversation_id;
          } else if (parsed.type === "token") {
            tokenCount += 1;
            fullText += parsed.text;
            callbacks.onToken?.(parsed.text);
          } else if (parsed.type === "done") {
            gotDone = true;
            conversationId = parsed.conversation_id;
            disarmWatchdog();
            callbacks.onDone?.({
              conversationId: parsed.conversation_id,
              citations: parsed.citations ?? [],
              truncated: parsed.truncated === true,
            });
          } else if (parsed.type === "error") {
            gotServerError = true;
            disarmWatchdog();
            callbacks.onError?.(parsed.message);
          }
        }
      };

      try {
        const url =
          import.meta.env.VITE_SUPABASE_URL + "/functions/v1/ai-router";

        // Watchdog covers the time-to-first-byte window, including the
        // fetch itself (cold starts).
        armWatchdog(FIRST_BYTE_TIMEOUT_MS);

        const resp = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            feature_key: "brain.query",
            org_id: orgId,
            inputs: {
              question: request.question,
              conversation_id: request.conversationId,
              conversation_history: request.conversationHistory,
              locale: request.locale,
            },
          }),
          signal: controller.signal,
        });

        if (!resp.ok) {
          disarmWatchdog();
          // Non-streaming error (auth failure, feature disabled, etc.).
          // Body is JSON: { error: "...", detail?: "..." }
          let detail = "";
          try {
            const errBody = await resp.json();
            detail = errBody?.detail ?? errBody?.error ?? "";
          } catch {
            detail = "HTTP " + resp.status;
          }
          callbacks.onError?.(detail || "Request failed");
          return;
        }

        if (!resp.body) {
          disarmWatchdog();
          callbacks.onError?.("No response body");
          return;
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Data arrived; re-arm the idle watchdog.
          armWatchdog(IDLE_TIMEOUT_MS);

          buffer += decoder.decode(value, { stream: true });

          // SSE event boundaries are blank lines (\n\n).
          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";

          for (const rawEvent of events) {
            processEvent(rawEvent);
          }
        }

        disarmWatchdog();

        // 6.7.5 fix: flush the decoder and process whatever is left in the
        // buffer. A final done event that arrives without a trailing blank
        // line before the connection closes used to be silently dropped.
        buffer += decoder.decode();
        if (buffer.trim().length > 0) {
          processEvent(buffer);
        }

        if (!gotDone && !gotServerError) {
          if (fullText.length > 0) {
            finishIncomplete(
              "closed_without_done",
              "Stream closed before the done event",
            );
          } else {
            callbacks.onError?.(
              "The stream ended before any content arrived",
            );
          }
        }
      } catch (err) {
        disarmWatchdog();
        if (userCancelledRef.current) return; // user cancelled deliberately

        const detail = err instanceof Error ? err.message : String(err);

        if (watchdogFiredRef.current) {
          if (fullText.length > 0) {
            finishIncomplete(
              "stalled",
              "No data received for " + IDLE_TIMEOUT_MS / 1000 + "s",
            );
          } else {
            callbacks.onError?.(
              "The request timed out before a response started",
            );
          }
        } else if (fullText.length > 0) {
          finishIncomplete("stream_error", detail);
        } else {
          callbacks.onError?.(detail);
        }
      } finally {
        disarmWatchdog();
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [orgId],
  );

  const cancel = useCallback(() => {
    userCancelledRef.current = true;
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  return { sendQuestion, isStreaming, cancel };
}
